import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import {
  getFirestore,
  collection,
  getDoc,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  addDoc,
  deleteDoc,
  writeBatch,
  limit,
  arrayUnion,
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import moment from 'moment-timezone';
import csv from 'csvtojson';
import { FIREBASE_CONFIG, outsideAssignmentWithTypeSite } from 'config/constants';
import store from '../store';

const getLoggedInUser = () => store.getState().account.data;
const getWebEnv = () => store.getState().app.env;
const firebaseConfig = FIREBASE_CONFIG;

// Initialize Firebase
let app;
let storage;
let db;
let functions;

let MAIL_DOMAIN = '';

const initializeFirebase = async (config) => {
  app = initializeApp(config);
  functions = getFunctions(app);

  storage = getStorage();
  db = getFirestore();
  getAnalytics(app);

  const webConfig = await getDoc(doc(db, 'config', 'webAdmin'));
  MAIL_DOMAIN = webConfig.data().mailDomain;
  return { ...webConfig.data(), projectId: config.projectId };
};

export const fetchAppConfig = async () => {
  if (process.env.NODE_ENV === 'development') {
    return initializeFirebase(firebaseConfig);
  } else {
    const response = await fetch('/__/firebase/init.json', { method: 'GET' });
    return initializeFirebase(await response.json());
  }
};

export const downloadImage = (url) => {
  return getDownloadURL(ref(storage, url));
};

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // console.log("user", user);
        const qEmployees = query(collection(db, 'employees'), where('email', '==', user.email));
        const snapshot = await getDocs(qEmployees);

        const isSPV = !snapshot.empty
          ? !(
              await getDocs(
                query(
                  collection(db, 'employees'),
                  where('superiorNip', '==', snapshot.docs[0].data().nip),
                  limit(1)
                )
              )
            ).empty
          : false;

        resolve({
          // access_token: user.accessToken,
          email: user.email,
          uid: user.uid,
          ...(!snapshot.empty
            ? {
                isHR: snapshot.docs[0].data().isHR || false,
                isSPV,
                ...snapshot.docs[0].data(),
              }
            : { isHR: false, isSPV: false, fullname: 'Unknown' }),
        });
      } else {
        reject('Not signed in'); // eslint-disable-line
      }
    });
  });
};

export const userSignIn = async (email, password) => {
  const auth = getAuth();

  await signInWithEmailAndPassword(auth, email, password).then(() => {
    return getCurrentUser();
  });
};

export const userSignOut = async () => {
  const auth = getAuth();
  await signOut(auth);
};

export const getEmployee = async (options) => {
  let q;

  if (options?.field) {
    q = query(
      collection(db, 'employees'),
      where(options?.field, options?.operator, options?.value)
    );
  } else {
    q = query(collection(db, 'employees')); //, orderBy("nip", "asc"));
  }

  const employeeSnapshot = await getDocs(q);
  return employeeSnapshot.docs.map((doc) => {
    return doc.data();
  });
};

export const getMaster = async (category) => {
  try {
    const qAdmin = query(collection(db, 'admin', 'masters', category), orderBy('name', 'asc'));
    const snapshot = await getDocs(qAdmin);
    return snapshot.docs.map((doc) => {
      return { id: doc.id, ...doc.data() };
    });
  } catch (err) {
    console.log({ err });
  }
};

export const saveMaster = async (category, draft, customCollection = null) => {
  const collectionPath = customCollection || `admin/masters/${category}`;
  const collectionRef = collection(db, collectionPath);

  const { id: draftId, ...restOfDraft } = draft;

  if (draftId) {
    await setDoc(doc(collectionRef, draftId), restOfDraft);
  } else {
    await addDoc(collectionRef, restOfDraft);
  }
};

export const deleteMaster = async (category, id) => {
  await deleteDoc(doc(db, 'admin', 'masters', category, id));
};

export const getDivision = async () => {
  const qAdmin = query(collection(db, 'admin', 'masters', 'divisions'));
  const divisionSnapshot = await getDocs(qAdmin);
  return divisionSnapshot.docs.map((doc) => {
    return doc.data();
  });
};

export const saveEmployee = async (employee) => {
  const env = getWebEnv();
  const DEFAULT_PASSWORD = '123456';
  const email = env?.usePrivateMail ? employee.privateEmail : employee.nip + MAIL_DOMAIN;
  const payload = {
    ...employee,
    email,
  };
  await setDoc(doc(db, 'employees', employee.nip), payload);
  if (employee.nip) {
    try {
      const auth = getAuth();
      await createUserWithEmailAndPassword(auth, email, DEFAULT_PASSWORD);
    } catch (err) {
      console.log(err);
    }
  }
};

export const newAccount = async (employee) => {
  const env = getWebEnv();
  const DEFAULT_PASSWORD = '123456';
  const auth = getAuth();
  const email = env?.usePrivateMail ? employee.privateEmail : employee.nip + MAIL_DOMAIN;
  const user = await createUserWithEmailAndPassword(auth, email, DEFAULT_PASSWORD);
  await saveEmployee(employee);
  await newUser(user?.user, employee);
  return user;
};

const newUser = async (user, employee) => {
  await setDoc(doc(db, 'users', user?.uid), {
    nip: `employees/${employee.nip}`,
  });
};

const saveUser = async (id, user) => {
  await setDoc(doc(db, 'users', id), user);
};

const getUserByNip = async (nip) => {
  const q = query(collection(db, 'users'), where('nip', '==', `employees/${nip}`));
  return await getDocs(q);
};

export const getEmployeeByUid = async (uid) => {
  const userSnapshot = await getDoc(doc(db, 'users', uid));

  const { nip } = userSnapshot.data();

  const employeeSnapshot = await getDoc(doc(db, nip));

  return employeeSnapshot.data();
};

export const changePassword = async (nip, newPassword) => {
  const saveChangePassword = httpsCallable(functions, 'changeUserPassword');

  return await saveChangePassword({
    nip,
    newPassword,
  });
};

export const changeNip = async (oldNip, newNip, mailDomain) => {
  try {
    const q = query(collection(db, 'employees'), where('nip', '==', newNip));
    const employeeSnapshot = await getDocs(q);

    if (employeeSnapshot.size > 0) {
      const existingUser = employeeSnapshot.docs[0].data();
      throw `NIP sudah digunakan oleh pegawai: ${existingUser.fullname} `;
    }

    const saveChangeNip = httpsCallable(functions, 'changeUserNip');

    return await saveChangeNip({
      oldNip,
      newNip,
      mailDomain,
    });
  } catch (error) {
    throw error;
  }
};

export const getUser = async (nip) => {
  const userSnapshot = await getUserByNip(nip);
  if (userSnapshot.size > 0) {
    return userSnapshot.docs[0].data();
  }
};

export const unlockDevice = async (nip) => {
  const userSnapshot = await getUserByNip(nip);
  userSnapshot.forEach((doc) => {
    const { deviceId, fcm, ...data } = doc.data();
    saveUser(doc.id, data);
  });
};

export const getShiftByNip = async (nip, firstDay, lastDay) => {
  const q = query(
    collection(db, 'shifts'),
    where('nip', '==', nip),
    where('shiftStartTime', '>=', firstDay),
    where('shiftStartTime', '<=', lastDay)
  );
  const querySnapshot = await getDocs(q);
  const data = querySnapshot.docs.map((doc) => {
    return { docKey: doc.id, ...doc.data() };
  });
  return data;
};

export const getShiftByDocKey = async (docKey) => {
  const querySnapshot = await getDoc(doc(db, 'shifts', docKey));
  return { docKey: querySnapshot.id, ...querySnapshot.data() };
};

export const getTodayShift = async (date) => {
  const startOfDay = moment(date).startOf('day').valueOf();
  const endOfDay = moment(date).endOf('day').valueOf();

  const q = query(
    collection(db, 'shifts'),
    where('shiftStartTime', '>=', startOfDay),
    where('shiftStartTime', '<=', endOfDay)
  );

  const querySnapshot = await getDocs(q);

  const data = querySnapshot.docs.map((doc) => ({
    docKey: doc.id,
    ...doc.data(),
  }));

  return data;
};

export const saveEmployeeShift = async (draft) => {
  const { docKey, name, insertTime, oldShift, ...payload } = draft;
  const [nip] = docKey.split('_');
  const { fullname, uid } = getLoggedInUser();

  const employeeName = payload.employee.fullname;
  delete payload.employee;
  delete payload.date;
  const source = 'web';

  if (!insertTime) {
    await setDoc(doc(db, 'shifts', docKey), {
      name,
      tolerance: 0,
      insertTime: new Date().getTime(),
      source,
      ...payload,
    });
  } else {
    await updateDoc(doc(db, 'shifts', docKey), {
      ...(name === 'SHIFT OFF' ? {} : { name }),
      insertTime: new Date().getTime(),
      source,
      ...payload,
    });
  }

  // update shift in exist attendance

  const { shiftStartTime, shiftEndTime } = payload;

  const q = query(
    collection(db, 'attendances'),
    where('nip', '==', nip),
    where('entryTime', '>=', moment(shiftStartTime).startOf('day').valueOf()),
    where('entryTime', '<', moment(shiftStartTime).endOf('day').valueOf())
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.size > 0) {
    Promise.all(
      querySnapshot.docs.map((item) =>
        updateDoc(doc(db, 'attendances', item.id), { shiftStartTime, shiftEndTime })
      )
    ).catch(() => {
      throw 'jam shift pada absen tidak dapat diupdate!';
    });
  }

  let timeStatus = 0;
  let shiftEndTimeforCheck = shiftEndTime;

  if (shiftStartTime > shiftEndTimeforCheck) {
    shiftEndTimeforCheck = moment(shiftEndTime).add(1, 'day').valueOf();
    timeStatus = '2';
  }

  let previousShift;

  if (oldShift.insertTime) {
    previousShift = {
      dayOff: oldShift.dayOff,
      insertTime: oldShift.insertTime,
      name: oldShift.name,
      nip: oldShift.nip,
      shiftEndTime: oldShift.shiftEndTime,
      shiftStartTime: oldShift.shiftStartTime,
      timeStatus: oldShift.timeStatus,
      tolerance: oldShift.tolerance,
    };
  } else {
    previousShift = null;
  }

  const data = {
    nip,
    employeeName,
    dayOff: payload.dayOff,
    name,
    shiftStartTime,
    shiftEndTime,
    timeStatus,
    shiftDate: shiftStartTime,
    oldShift: previousShift,
    userChanger: fullname,
    uid,
    insertTime: new Date().getTime(),
  };

  addDoc(collection(db, 'switchShift'), {
    ...data,
    isChangeShift: true,
  });
};

export const getAttendanceByDate = async (startDate) => {
  try {
    const endDate = moment(startDate).endOf('day').valueOf();

    const q = query(
      collection(db, 'attendances'),
      where('entryTime', '>=', startDate),
      where('entryTime', '<', endDate)
    );
    const snapshots = await getDocs(q);

    return snapshots.docs.map((doc) => {
      return { ...doc.data(), attendanceId: doc.id };
    });
  } catch (err) {
    console.log(err);
  }
};

export const getAttendanceByNip = async (nip, selectedTime) => {
  try {
    const start = moment(selectedTime).startOf('month').valueOf();
    const end = moment(selectedTime).endOf('month').valueOf();

    const q = query(
      collection(db, 'attendances'),
      where('nip', '==', nip),
      where('entryTime', '>=', start),
      where('entryTime', '<=', end),
      orderBy('entryTime', 'asc')
    );
    const snapshots = await getDocs(q);

    console.log(`${snapshots.size} attendances`);

    const fetchAllMetadata = snapshots.docs.map(async (doc) => {
      const attendanceData = doc.data();
      let entryPhotoUrl = '';
      let exitPhotoUrl = '';
      let morningAssemblyPhotoUrl = '';

      if (attendanceData.entryPhotoUrl) {
        entryPhotoUrl = attendanceData.entryPhotoUrl;
      } else if (attendanceData.entryPhoto) {
        const url = await getDownloadURL(
          ref(storage, `attendances/${doc.id}/${attendanceData.entryPhoto}`)
        ).catch((error) => {
          // console.log(error);
          return '';
        });
        entryPhotoUrl = url;
      }

      if (attendanceData.exitPhotoUrl) {
        exitPhotoUrl = attendanceData.exitPhotoUrl;
      } else if (attendanceData.exitPhoto) {
        const url = await getDownloadURL(
          ref(storage, `attendances/${doc.id}/${attendanceData.exitPhoto}`)
        ).catch((error) => {
          // console.log(error);
          return '';
        });
        exitPhotoUrl = url;
      }

      if (attendanceData.morningAssemblyPhotoUrl) {
        morningAssemblyPhotoUrl = attendanceData.morningAssemblyPhotoUrl;
      } else if (attendanceData.morningAssemblyPhoto) {
        const url = await getDownloadURL(
          ref(storage, `attendances/${doc.id}/${attendanceData.morningAssemblyPhoto}`)
        ).catch((error) => {
          // console.log(error);
          return '';
        });
        morningAssemblyPhotoUrl = url;
      }

      return {
        ...attendanceData,
        attendanceId: doc.id,
        entryPhotoUrl,
        exitPhotoUrl,
        morningAssemblyPhotoUrl,
      };
    });

    const userAttendanceData = await Promise.all(fetchAllMetadata);
    return userAttendanceData;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getPendingRequest = async ({ start, end }) => {
  try {
    const { nip, isHR, access } = getLoggedInUser();

    const queryStart = start ? start.valueOf() : moment().startOf('month').valueOf();
    const queryEnd = end ? end.valueOf() : moment().endOf('month').valueOf();

    const viewAllList = isHR || (access && Array.isArray(access) && access.includes('transaction'));
    const viewMahasiswaList = nip === 'admin_mahasiswa' && isHR;
    const leaveTypes = {};

    const attendanciesDocs = await getDocs(
      query(collection(db, 'admin', 'masters', 'attendancies'), where('type', '==', 4))
    );

    attendanciesDocs.docs.forEach((item) => {
      Object.assign(leaveTypes, { [item.id]: item.data().name });
    });

    const getLeaveQuery = (leaveType) => {
      const collectionRef = collection(db, leaveType);
      const queryList = [where('startTime', '>=', queryStart), where('startTime', '<', queryEnd)];

      if (!viewAllList) {
        queryList.push(where('superiorNip', '==', nip));
      }
      return query(collectionRef, ...queryList);
    };

    const leaveQuery = getLeaveQuery('leaves');
    const sickLeaveQuery = getLeaveQuery('sickLeaves');
    const suddenLeaveQuery = getLeaveQuery('suddenLeaves');
    const outsideAssignmentQuery = getLeaveQuery('outsideAssignment');

    const fetchAllQuery = await Promise.all([
      getDocs(leaveQuery),
      getDocs(sickLeaveQuery),
      getDocs(suddenLeaveQuery),
      getDocs(outsideAssignmentQuery),
    ]);

    const mapEmployeeData = (category) => async (item) => {
      const user = await getDoc(doc(db, 'users', item.data().uid));
      let employee;

      if (category === 'outsideAssignment') {
        const nip = (await getDoc(doc(db, 'users', item.data().employeeId)))
          .data()
          .nip.split('/')[1];

        employee = await getDoc(doc(db, 'employees', nip));
      } else {
        employee = await getDoc(doc(db, 'employees', item.data().nip));
      }

      if (user !== undefined) {
        return {
          category,
          id: item.id,
          ...item.data(),
          startTime: item.data().startTime || item.data().timestamp,
          typeName:
            item.data().typeName ||
            leaveTypes[item.data().type] || // TODO: need to refactor this after all site migrated.
            (item.data().category === 'outsideAssignment' ?? 'Tugas Luar'),
          employee: {
            uid: item.data().uid,
            nip: employee.id,
            photoUrl: user.data()?.photoUrl || null,
            ...employee.data(),
          },
        };
      } else return null;
    };

    const fetchAllLeaveMetadata = fetchAllQuery[0].docs.map(mapEmployeeData('leave'));

    const fetchAllSickLeaveMetadata = fetchAllQuery[1].docs.map(mapEmployeeData('sickLeave'));

    const fetchAllSuddenLeaveMetadata = fetchAllQuery[2].docs.map(mapEmployeeData('suddenLeave'));

    const fetchAllOutsideAssignmentMetadata = fetchAllQuery[3].docs.map(
      mapEmployeeData('outsideAssignment')
    );

    const allFetchedData = await Promise.all([
      Promise.all(fetchAllLeaveMetadata),
      Promise.all(fetchAllSickLeaveMetadata),
      Promise.all(fetchAllSuddenLeaveMetadata),
      Promise.all(fetchAllOutsideAssignmentMetadata),
    ]);

    const data = [
      ...allFetchedData[0],
      ...allFetchedData[1],
      ...allFetchedData[2],
      ...allFetchedData[3],
    ]
      .filter((e) => !!e)
      .sort((a, b) => b.timestamp - a.timestamp);

    if (viewMahasiswaList) {
      return data.filter((val) => val.employee.division === 'Mahasiswa');
    }

    return data;
  } catch (err) {
    console.log('getPendingRequest', err);
  }
};

export const setLeaveRequestStatus = async ({ employeeUid, id, status, item, ...payload }) => {
  const { projectId } = getWebEnv();
  const { nip, fullname } = getLoggedInUser();

  const existingReviews = item.reviews || [];
  const reviews = [
    ...existingReviews,
    {
      approved: status === 'approved',
      rejected: status === 'rejected',
      ...(status === 'rejected' && { rejectReason: payload.rejectionMessage }),
      reviewedAt: Date.now(),
      reviewerId: nip,
      reviewerName: fullname,
      source: 'web',
    },
  ];
  delete payload.rejectionMessage;

  const data = {
    status,
    reviews,
    ...payload,
    updatedAt: Date.now(),
  };

  // Check if employee already has attendance today
  if (item && item.startTime === new Date().setHours(0, 0, 0, 0) && status === 'approved') {
    const todaysAttendance = await getDocs(
      query(
        collection(db, 'attendances'),
        where('nip', '==', item.nip),
        where('entryTime', '>=', new Date().setHours(0, 0, 0, 0)),
        where('entryTime', '<', new Date().setHours(23, 59, 0, 0))
      )
    );

    if (todaysAttendance.size > 0) {
      throw 'Pegawai yang mengajukan cuti telah absen hari ini';
    }
  }

  // check if employee already has outside assignment
  if (status === 'approved' && projectId === 'TBA') {
    const start = moment(item.startTime).subtract(1, 'month').valueOf();

    const q = query(
      collection(db, 'outsideAssignment'),
      where('employeeId', '==', item.uid),
      where('startTime', '>=', start),
      where('startTime', '<=', item.endTime),
      where('status', '==', 'approved')
    );
    const snapshots = await getDocs(q);

    const overlappedDateRange = snapshots.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((x) => {
        if (
          moment(item.startTime).isBetween(moment(x.startTime), moment(x.endTime), 'day', '[]') ||
          moment(item.endTime).isBetween(moment(x.startTime), moment(x.endTime), 'day', '[]')
        ) {
          return true;
        }
        return false;
      });

    if (overlappedDateRange.length > 0) {
      const takenAssignment = overlappedDateRange[0];
      throw `Pegawai memiliki Tugas Luar pada tanggal ${moment(takenAssignment.startTime).format(
        'DD MMMM YYYY'
      )} s/d ${moment(takenAssignment.endTime).format('DD MMMM YYYY')}`;
    }
  }

  await updateDoc(doc(db, 'leaves', id), data);
};

export const setSickLeaveRequestStatus = async ({ employeeUid, id, status, item, ...payload }) => {
  const { nip, fullname } = getLoggedInUser();

  const existingReviews = item.reviews || [];
  const reviews = [
    ...existingReviews,
    {
      approved: status === 'approved',
      rejected: status === 'rejected',
      ...(status === 'rejected' && { rejectReason: payload.rejectionMessage }),
      reviewedAt: Date.now(),
      reviewerId: nip,
      reviewerName: fullname,
    },
  ];
  delete payload.rejectionMessage;

  const data = {
    status,
    reviews,
    ...payload,
    updatedAt: Date.now(),
  };

  await updateDoc(doc(db, 'sickLeaves', id), data);
};

export const setSuddenLeaveRequestStatus = async ({
  employeeUid,
  id,
  status,
  item,
  ...payload
}) => {
  const { nip, fullname } = getLoggedInUser();

  const existingReviews = item.reviews || [];
  const reviews = [
    ...existingReviews,
    {
      approved: status === 'approved',
      rejected: status === 'rejected',
      ...(status === 'rejected' && { rejectReason: payload.rejectionMessage }),
      reviewedAt: Date.now(),
      reviewerId: nip,
      reviewerName: fullname,
    },
  ];
  delete payload.rejectionMessage;

  const data = {
    status,
    reviews,
    ...payload,
    updatedAt: Date.now(),
  };

  await updateDoc(doc(db, 'suddenLeaves', id), data);
};

export const setOutsideAssignmentRequestStatus = async ({ id, status, item, ...payload }) => {
  const { nip, fullname } = getLoggedInUser();

  const existingReviews = item.reviews || [];
  const reviews = [
    ...existingReviews,
    {
      approved: status === 'approved',
      rejected: status === 'rejected',
      ...(status === 'rejected' && { rejectReason: payload.rejectionMessage }),
      reviewedAt: Date.now(),
      reviewerId: nip,
      reviewerName: fullname,
    },
  ];
  delete payload.rejectionMessage;

  const data = {
    status,
    reviews,
    updatedAt: Date.now(),
  };

  await updateDoc(doc(db, 'outsideAssignment', id), data);
};

export const editLeaveData = async (item) => {
  const { category, id, changeStatusMessage, status, startTime, endTime, employee, type } = item;
  const { nip, fullname } = getLoggedInUser();

  const updatedAt = Date.now();
  let totalDays = 0;

  const reviews = [
    {
      approved: status === 'approved',
      rejected: status === 'rejected',
      reviewedAt: Date.now(),
      reviewerId: nip,
      reviewerName: fullname,
    },
  ];

  const body = {
    startTime,
    endTime,
    status,
    ...(status !== 'pending' && { reviews: arrayUnion(...reviews) }),
    userChanger: fullname,
    changeStatusMessage,
    updatedAt,
  };

  if (category === 'leave') {
    const { projectId } = getWebEnv();
    const maternityLeaveCode = projectId === 'atmost-parigi' ? 27 : 26;

    if (+type !== maternityLeaveCode) {
      totalDays = await getTotalLeaveDay(employee.nip, startTime, endTime);
    } else {
      totalDays = 90;
    }

    if (status === 'approved') {
      const payload = { ...item, updatedAt, changeStatusMessage, userChanger: fullname };
      payload.days = totalDays;
      delete payload.employee;
      delete payload.category;
      delete payload.reviewerName;
      // delete payload.typeName;
      delete payload.id;

      return updateDoc(doc(db, 'leaves', id), {
        status: 'edited',
        isHistory: true,
      }).then(() => {
        addDoc(collection(db, 'leaves'), { ...payload, reviews, referenceId: id });
      });
    } else {
      return updateDoc(doc(db, 'leaves', id), {
        ...body,
        days: totalDays,
      });
    }
  } else if (category === 'outsideAssignment') {
    return updateDoc(doc(db, 'outsideAssignment', id), body);
  } else {
    return updateDoc(doc(db, `${category}s`, id), body);
  }
};

export const getAppealRequest = async (status) => {
  try {
    const twoMonthBefore = moment().subtract(2, 'month').valueOf();

    const q = query(
      collection(db, 'appeals'),
      where('timestamp', '>=', twoMonthBefore),
      ...(status ? [where('status', '==', status.toLowerCase())] : [])
    );
    const querySnapshot = await getDocs(q);

    const data = await Promise.all(
      querySnapshot.docs.map(async (item) => {
        const userRef = doc(db, 'users', item.data().uid);
        const employeeRef = doc(db, 'employees', item.data().nip);
        const [userDoc, employeeDoc] = await Promise.all([getDoc(userRef), getDoc(employeeRef)]);

        if (userDoc.exists()) {
          const user = userDoc.data();
          const employee = employeeDoc.data();

          return {
            id: item.id,
            ...item.data(),
            startTime: item.data().startTime || item.data().timestamp,
            employee: {
              uid: item.data().uid,
              nip: employeeDoc.id,
              photoUrl: user.photoUrl || null,
              ...employee,
            },
          };
        } else {
          return null;
        }
      })
    );

    return data.filter((item) => item !== null).sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.log(error);
  }
};

export const setAppealRequestStatus = async ({ employeeUid, id, status, ...payload }) => {
  const { currentUser } = getAuth();
  const data = {
    reviewerId: currentUser.uid,
    reviewedAt: Date.now(),
    status,
    ...payload,
  };

  await updateDoc(doc(db, 'appeals', id), data);
};

export const importShiftFile = (file) => {
  return new Promise((resolve, reject) => {
    const { timezone } = getWebEnv();
    const reader = new FileReader();
    reader.onload = async (e) => {
      const json = await csv({ delimiter: [';', ','] }).fromString(reader.result);

      const chunks = [];
      let chunksIndex = 0;
      while (json.length > 500) {
        chunks[chunksIndex] = json.splice(0, 500);
        chunksIndex++;
      }

      chunks[chunksIndex] = json;

      for (const chunk of chunks) {
        let batch = writeBatch(db);

        chunk.forEach(async (item) => {
          /* eslint-disable-line no-loop-func */
          if (item.STS_AKTIF === '1') {
            try {
              const shiftStartTime = moment
                .tz(item.TIME_COME, 'MM/DD/YYYY HH:mm:ss', timezone)
                .valueOf();
              const shiftEndTime = moment
                .tz(item.TIME_HOME, 'MM/DD/YYYY HH:mm:ss', timezone)
                .valueOf();
              const tolerance = moment
                .tz(item.TIME_TOLERANCE, 'MM/DD/YYYY HH:mm:ss', timezone)
                .valueOf();

              const data = {
                nip: item.NIP_NEW.toString(),
                shiftStartTime,
                shiftEndTime,
                tolerance,
                name: item.NM_GROUP,
                timeStatus: item.TIME_STATUS,
                dayOff: item.KD_SHIFT === '3',
                insertTime: new Date().getTime(),
              };

              const docKey =
                item.NIP_NEW +
                '_' +
                moment.tz(item.TIME_COME, 'MM/DD/YYYY HH:mm:ss', timezone).format('DDMMYYYY');
              const shiftRef = doc(db, 'shifts', docKey);
              batch.set(shiftRef, data);
            } catch (error) {
              console.error('Error writing document: ', error);
            }
          }
        });

        await batch.commit();
        batch = null;
      }

      resolve();
    };
    reader.readAsText(file);
  });
};

export const rebridgeAttendance = async (id) => {
  await updateDoc(doc(db, 'attendances', id), {
    lastBridge: new Date().getTime(),
  });
};

export const batchReBridgeAttendance = async (nip, seletedMonth) => {
  try {
    const batch = writeBatch(db);

    const firstDay = moment(moment(seletedMonth).startOf('month'))
      .startOf('day')
      .toDate()
      .getTime();
    const lastDay = moment(moment(seletedMonth).endOf('month')).endOf('day').toDate().getTime();

    const q = query(
      collection(db, 'attendances'),
      where('nip', '==', nip),
      where('entryTime', '>=', firstDay - 86400000),
      where('entryTime', '<=', lastDay + 86400000)
    );
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach(async (item) => {
      const attendanceRef = doc(db, 'attendances', item.id);
      batch.update(attendanceRef, {
        lastBridge: new Date().getTime(),
      });
    });

    batch.commit();
  } catch (err) {
    console.log(err);
  }
};

export const hideAttendanceItem = async (item) => {
  await updateDoc(doc(db, 'attendances', item.attendanceId), {
    hidden: true,
    nip: `${item.nip}_HIDDEN`,
    uid: `${item.uid}_HIDDEN`,
  });
};

export const addAttendance = async (item) => {
  const payload = item;
  const { fullname, uid } = getLoggedInUser();

  const employeeName = payload.employeeName;
  delete payload.employeeName;

  if (!payload.uid) {
    const userSnapshot = await getUserByNip(item.nip);
    const uid = userSnapshot.docs[0].id;

    if (!uid || userSnapshot.size === 0) throw 'uid karyawan tidak ditemukan';
    payload.uid = uid;
  }

  await addDoc(collection(db, 'attendances'), payload);

  const data = {
    nip: payload.nip,
    employeeName,
    ...(payload.entryTime ? { entryTime: payload.entryTime } : {}),
    ...(payload.exitTime ? { exitTime: payload.exitTime } : {}),
    uid,
    userChanger: fullname,
    insertTime: new Date().getTime(),
  };

  addDoc(collection(db, 'changeAttendanceHistories'), {
    ...data,
    isChangeAttendance: false,
  });
};

export const editAttendanceItem = async (item) => {
  const { fullname, uid } = getLoggedInUser();

  await updateDoc(doc(db, 'attendances', item.attendanceId), {
    ...(item.entryTime ? { entryTime: item.entryTime } : {}),
    ...(item.exitTime
      ? {
          exitTime: item.exitTime,
          exitCoordinate: item.exitCoordinate ? item.exitCoordinate : item.entryCoordinate || {},
          exitAddress: item.exitAddress ? item.exitAddress : item.entryAddress || '',
        }
      : {}),
    ...(item.isEntryEdit
      ? { isEntryEdit: item.isEntryEdit, entryTimePrev: item.initialData.entryTime }
      : {}),
    ...(item.isExitEdit
      ? { isExitEdit: item.isExitEdit, exitTimePrev: item.initialData.exitTime }
      : {}),
  });

  const data = {
    nip: item.nip,
    employeeName: item.employeeName,
    ...(item.entryTime ? { entryTime: item.entryTime } : {}),
    ...(item.exitTime ? { exitTime: item.exitTime } : {}),
    uid,
    userChanger: fullname,
    previousData: {
      ...(item.initialData?.entryTime ? { entryTime: item.initialData.entryTime } : {}),
      ...(item.initialData?.exitTime ? { exitTime: item.initialData.exitTime } : {}),
    },
    insertTime: new Date().getTime(),
  };

  addDoc(collection(db, 'changeAttendanceHistories'), {
    ...data,
    isChangeAttendance: true,
  });
};

export const getDevices = async () => {
  try {
    const q = query(collection(db, 'users'), where('deviceId', '!=', ''), orderBy('deviceId'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      return { ...doc.data(), uid: doc.id };
    });
  } catch (err) {
    console.log(err);
  }
};

export const getDevicesTester = async () => {
  try {
    const q = query(collection(db, 'config/testing/deviceId'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      return { ...doc.data(), deviceId: doc.id };
    });
  } catch (err) {
    console.log(err);
  }
};

export const unlockDeviceByUID = async (uid) => {
  await updateDoc(doc(db, 'users', uid), { deviceId: '' });
};

export const deleteTesterDeviceId = async (deviceId) => {
  await deleteDoc(doc(db, 'config/testing/deviceId', deviceId));
};

export const getStorageFileUri = async (item) => {
  const category = item.category === 'outsideAssignment' ? item.category : item.category + 's';

  return await getDownloadURL(
    ref(storage, `${category}/${item.referenceId ? item.referenceId : item.id}/${item.file}`)
  );
};

export const getUserActivityByUid = async (uid, selectedTime) => {
  try {
    const start = moment(selectedTime).startOf('month').valueOf();
    const end = moment(selectedTime).endOf('month').valueOf();

    const q = query(
      collection(db, 'activities'),
      where('uid', '==', uid),
      where('timestamp', '>=', start),
      where('timestamp', '<=', end)
    );
    const snapshots = await getDocs(q);

    console.log(`${snapshots.size} activities`);

    const data = await Promise.all(
      snapshots.docs.map(async (doc) => {
        const activities = doc.data();
        const files = activities?.files ? activities.files : [];
        return { ...doc.data(), activityId: doc.id, files };
      })
    );
    return data;
  } catch (err) {
    console.log(err);
  }
};

export const saveConfig = async (payload) => {
  return await updateDoc(doc(db, 'config', 'webAdmin'), payload);
};

export const getHolidayList = async () => {
  const startOfYear = moment().startOf('year').valueOf();
  const endOfYear = moment().endOf('year').add(1, 'week').valueOf();

  const querySnapshot = await getDocs(
    query(
      collection(db, 'holiday'),
      where('startDate', '>=', startOfYear),
      where('startDate', '<=', endOfYear)
    )
  );

  let masterShift = [];
  if (!querySnapshot.empty) {
    masterShift = await getMaster('shifts');
  }

  const data = querySnapshot.docs.map((item) => {
    const holidayData = item.data();
    const shifts = holidayData.shifts
      ? holidayData.shifts.map((shftId) => masterShift.find((mShift) => mShift.id === shftId))
      : [];
    return {
      holidayId: item.id,
      ...holidayData,
      shifts,
    };
  });

  return data;
};

export const updateHolidayShift = async (name, startTime, endTime, dayOff, item) => {
  try {
    const startOfTime = moment(new Date(startTime)).startOf('day').valueOf();
    const endOfTime = moment(new Date(endTime)).endOf('day').valueOf();
    const q = query(
      collection(db, 'shifts'),
      where('name', '==', name),
      where('shiftStartTime', '>=', startOfTime),
      where('shiftStartTime', '<=', endOfTime),
      where('dayOff', '==', !dayOff)
    );
    const querySnapshot = await getDocs(q);

    const updatePromises = querySnapshot.docs.map(async (snap) => {
      const docRef = doc(db, 'shifts', snap.id);
      const updateData = {
        dayOff,
        insertTime: new Date().getTime(),
      };
      if (dayOff) {
        updateData.note = 'update via sync';
      }
      await updateDoc(docRef, updateData);
    });

    await Promise.all(updatePromises);

    if (!dayOff) {
      const newShiftIds = item.shifts.filter((shift) => shift.name !== name).map(({ id }) => id);
      const holidayDocRef = doc(db, 'holiday', item.holidayId);
      await updateDoc(holidayDocRef, {
        shifts: newShiftIds,
      });
    }
    return querySnapshot.size;
  } catch (err) {
    console.log('gagal update tanggal libur' + err);
  }
};

export const addHolidayShift = async (holiday, newShift) => {
  try {
    const { holidayId, startDate, endDate } = holiday;
    const shiftIds = newShift.map(({ id }) => id);
    const shiftNames = newShift.map(({ name }) => name);
    const holidayDocRef = doc(db, 'holiday', holidayId);
    const holidayDocData = (await getDoc(holidayDocRef)).data();
    const currentShiftIds = holidayDocData.shifts ?? [];
    const uniqueShiftIds = Array.from(new Set([...currentShiftIds, ...shiftIds]));

    await updateDoc(holidayDocRef, { shifts: uniqueShiftIds });

    const metadataPromises = shiftNames.map((name) =>
      updateHolidayShift(name, startDate, endDate, true, holiday)
    );
    await Promise.all(metadataPromises);
    return;
  } catch (err) {
    console.log(err);
  }
};

export const addHoliday = async (payload) => {
  const startDate = moment(payload.startDate).startOf('day').valueOf();
  const endDate = moment(payload.endDate).endOf('day').valueOf();
  const holidayData = {
    ...payload,
    startDate,
    endDate,
    updatedAt: new Date().getTime(),
  };

  await addDoc(collection(db, 'holiday'), holidayData);
};

export const editHoliday = async (item) => {
  const { holidayId, ...payload } = item;
  const { shifts, startDate, endDate, ...restPayload } = payload;

  const holidayDocRef = doc(db, 'holiday', holidayId);

  return await updateDoc(holidayDocRef, restPayload);
};

export const deleteHoliday = async (item) => {
  const { holidayId, startDate, endDate, shifts } = item;
  const holidayDocRef = doc(db, 'holiday', holidayId);
  const shiftsNames = shifts?.map(({ name }) => name) ?? [];

  const metadataPromises = shiftsNames.map((name) =>
    updateHolidayShift(name, startDate, endDate, false, item)
  );
  await Promise.all(metadataPromises);

  return deleteDoc(holidayDocRef);
};

export const addTransaction = async (employee, item, userAccount) => {
  const { projectId } = getWebEnv();
  const { nip, fullname, superiorNip } = employee;
  const {
    leave: { type, name },
    startTime,
    endTime,
    taskDescription,
    ...payload
  } = item;

  const currentTime = new Date().getTime();

  const docName = name === 'Izin Sakit' ? 'sickLeaves' : 'leaves';

  const user = await getUserByNip(nip);
  const uid = user.docs[0].id || employee.uid;

  const reviews = [
    {
      approved: true,
      rejected: false,
      reviewedAt: currentTime,
      reviewerId: userAccount.nip,
      reviewerName: userAccount.fullname,
    },
  ];

  if (projectId === 'atmost-bogor' && type === '03') {
    const year = moment(startTime).format('YYYY');

    const leaveBalanceDocRef = doc(db, `employees/${nip}/leaveBalance`, year);
    const snapshot = await getDoc(leaveBalanceDocRef);

    if (snapshot.exists()) {
      const previousValue = snapshot.data().used || 0;
      await updateDoc(leaveBalanceDocRef, { used: previousValue + item.days });
    }
  }

  return addDoc(collection(db, docName), {
    ...payload,
    uid,
    nip,
    fullname,
    superiorNip: superiorNip || null,
    startTime,
    endTime,
    isHrRequest: !!employee.isHR,
    reviews,
    file: null,
    step: 0,
    status: 'approved',
    manualInput: true,
    timestamp: currentTime,
    updatedAt: currentTime,
    ...(docName === 'leaves' && {
      type,
      typeName: name,
      taskDescription,
      leaveAddress: null,
      replacementId: null,
      replacementName: null,
      emergencyPhoneNo: null,
    }),
  });
};

export const addOutsideAssignment = async (employee, item, userAccount) => {
  const { projectId } = getWebEnv();
  const { uid, nip, fullname, superiorNip } = employee;
  const { accomodationType, transportationType, type, ...payload } = item;

  const currentTime = Date.now();

  const reviews = [
    {
      approved: true,
      rejected: false,
      reviewedAt: currentTime,
      reviewerId: userAccount.nip,
      reviewerName: userAccount.fullname,
    },
  ];

  return addDoc(collection(db, 'outsideAssignment'), {
    ...payload,
    uid: userAccount.uid,
    employeeId: uid,
    nip,
    fullname,
    superiorNip,
    isHrRequest: !!employee.isHR,
    isSelfAssign: false,
    reviews,
    file: null,
    step: 0,
    status: 'approved',
    manualInput: true,
    timestamp: currentTime,
    updatedAt: currentTime,
    accomodationType: accomodationType.id,
    transportationType: transportationType.id,
    ...(outsideAssignmentWithTypeSite.includes(projectId) && { type: type.id }),
  });
};

export const getTotalLeaveDay = async (nip, startDate, endDate) => {
  let totalDays = 0;
  const q = query(
    collection(db, 'shifts'),
    where('nip', '==', nip),
    where('shiftStartTime', '>=', startDate),
    where('shiftStartTime', '<=', endDate)
  );
  let shiftSnapshot = await getDocs(q);

  shiftSnapshot = shiftSnapshot.docs.map((doc) => {
    return { id: doc.id, ...doc.data() };
  });

  if (shiftSnapshot.length === 0) {
    if (moment(startDate).get('DDMMYYYY') === moment(endDate).get('DDMMYYYY')) {
      totalDays = 1;
    } else {
      const currentDate = moment(startDate);
      // eslint-disable-next-line no-unmodified-loop-condition
      while (currentDate <= endDate) {
        const currentDay = currentDate.get('day');
        if (!(currentDay === 0 || currentDay === 6)) {
          totalDays++;
        }
        currentDate.add(1, 'day');
      }
    }
  } else {
    totalDays = shiftSnapshot.filter((doc) => !doc.dayOff).length;
  }

  return totalDays;
};

export const getUserLeaveByNip = async (nip, startDate, endDate) => {
  const collectionsName = ['leaves', 'sickLeaves', 'suddenLeaves'];
  const alluserLeaves = [];
  const queries = collectionsName.map((coll) =>
    getDocs(
      query(
        collection(db, coll),
        where('startTime', '>=', startDate),
        where('startTime', '<=', endDate),
        where('nip', '==', nip)
      )
    )
  );

  const fetchAllQuery = await Promise.all(queries);
  const getData = (i) =>
    fetchAllQuery[i].docs
      .filter((snap) => snap.data().status !== 'rejected')
      .map((snap) => ({ id: snap.id, ...snap.data() }));

  return alluserLeaves.concat(getData(0), getData(1), getData(2));
};

const getUsedAnnualLeave = async (nip) => {
  const startOfYear = moment().startOf('year').valueOf();
  const endOfYear = moment().endOf('year').valueOf();

  const q = query(
    collection(db, 'leaves'),
    where('nip', '==', nip),
    where('type', '==', '03'),
    where('startTime', '>=', startOfYear),
    where('startTime', '<=', endOfYear),
    where('status', '==', 'approved')
  );

  const snapshots = await getDocs(q);

  const totalDays = snapshots?.docs.reduce((sum, doc) => {
    const data = doc.data();
    return sum + (data.days ?? 0);
  }, 0);

  return totalDays;
};

export const getUserLeaveBalance = async (nip) => {
  try {
    const currentYear = new Date().getFullYear().toString();
    const lastYear = (currentYear - 1).toString();
    const lastTwoYears = (currentYear - 2).toString();

    const lastTwoYearsQuery = await getDoc(doc(db, `employees/${nip}/leaveBalance`, lastTwoYears));
    const lastYearQuery = await getDoc(doc(db, `employees/${nip}/leaveBalance`, lastYear));
    const currentYearQuery = await getDoc(doc(db, `employees/${nip}/leaveBalance`, currentYear));

    const lastTwoYearsBalance = lastTwoYearsQuery.data()?.remaining || 0;
    const lastYearBalance = lastYearQuery.data()?.remaining || 0;
    const currentYearBalance = currentYearQuery.data()?.remaining || 0;
    const additionalBalance = currentYearQuery.data()?.additional || 0;

    let used = 0;
    if (currentYearQuery.data()?.used !== undefined) {
      used = currentYearQuery.data()?.used;
    } else {
      used = await getUsedAnnualLeave(nip);
    }

    return {
      lastTwoYearsBalance,
      lastYearBalance,
      currentYearBalance,
      additionalBalance,
      used,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const updateLeaveBalance = async (nip, field, year, value) => {
  try {
    await updateDoc(doc(db, `employees/${nip}/leaveBalance`, year), {
      [field]: value,
    });
  } catch (error) {
    throw error;
  }
};

export const getUserLeaveHistory = async (nip) => {
  try {
    const startOfYear = moment().startOf('year').valueOf();
    const endOfYear = moment().endOf('year').valueOf();

    const q = query(
      collection(db, 'leaves'),
      where('nip', '==', nip),
      where('type', '==', '03'),
      where('startTime', '>=', startOfYear),
      where('startTime', '<=', endOfYear),
      where('status', '==', 'approved')
    );
    const snapshots = await getDocs(q);

    console.log(`${snapshots.size} leaves`);

    if (!snapshots.empty) {
      return snapshots.docs.map((doc) => ({ id: doc.id, category: 'leave', ...doc.data() }));
    } else {
      return [];
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const setAdditionalLeaveBalance = async (payload, previousValue) => {
  try {
    const { nip, value, year, ...rest } = payload;

    await updateDoc(doc(db, `employees/${nip}/leaveBalance`, year), {
      additional: previousValue + value,
    });

    await addDoc(collection(db, 'additionalLeaveHistories'), {
      nip,
      days: value,
      year,
      ...rest,
      timestamp: Date.now(),
    });
  } catch (error) {
    throw error;
  }
};
